import { FC, Ref, useRef } from "react";

import { SidebarOptionsMenu, SidebarOptionsMenuProps } from "./SidebarOptionsMenu";

import { CopyToCourseSubMenu } from "components/menus/CopyToSectionSubMenu";
import { t } from "i18n/i18n";
import { IconText } from "mds/components/IconText";
import { MenuDivider, type MenuInstance, MenuItem, SubMenu } from "mds/components/Menu";
import {
  CopySendArrowIcon,
  EyeNotVisibleIcon,
  PageListCheckIcon,
  PageListIcon,
  UndoIcon,
} from "mds/icons";
import { BUTTON_EVENT_CATEGORY, trackEvent } from "utils/amplitude";

interface TopicSidebarOptionsMenuProps extends SidebarOptionsMenuProps {
  optionsMenuActions: SidebarOptionsMenuProps["optionsMenuActions"] & {
    onCopyToCourse?: (courseIds: string[]) => void;
    onChangeStatus: () => void;
    onChangeCompletedAt?: () => void;
  };
  releasedCount?: number;
  unreleasedCount?: number;
  completedAt?: string | null;
}

export const TopicSidebarOptionsMenu: FC<TopicSidebarOptionsMenuProps> = ({
  styles,
  size = "s",
  className,
  direction = "right",
  releasedCount,
  unreleasedCount,
  completedAt,
  optionsMenuActions,
}) => {
  const menuRef: Ref<MenuInstance> = useRef(null);
  const topicIsUnreleased = releasedCount === 0;
  const releaseStatus = unreleasedCount > 0 ? "unreleased" : "released";

  return (
    <SidebarOptionsMenu
      className={className}
      direction={direction}
      instanceRef={menuRef}
      optionsMenuActions={optionsMenuActions}
      size={size}
      styles={styles}
    >
      {releaseStatus === "released" ? (
        <MenuItem
          title={t("sidebar_options_menu.tooltip.unrelease_pages")}
          onClick={optionsMenuActions.onChangeStatus}
        >
          <IconText
            iconStart={<EyeNotVisibleIcon />}
            text={t("sidebar_options_menu.unrelease_pages")}
          />
        </MenuItem>
      ) : (
        <MenuItem
          disabled={!releaseStatus}
          title={t(`sidebar_options_menu.tooltip.${releaseStatus ? "release_pages" : "no_pages"}`)}
          onClick={optionsMenuActions.onChangeStatus}
        >
          <IconText iconStart={<PageListIcon />} text={t("sidebar_options_menu.release_pages")} />
        </MenuItem>
      )}

      {!topicIsUnreleased && optionsMenuActions?.onChangeCompletedAt && completedAt ? (
        <MenuItem onClick={optionsMenuActions.onChangeCompletedAt}>
          <IconText
            iconStart={<UndoIcon />}
            text={t("sidebar_options_menu.mark_as_complete_undo")}
          />
        </MenuItem>
      ) : (
        !topicIsUnreleased &&
        optionsMenuActions?.onChangeCompletedAt && (
          <MenuItem onClick={optionsMenuActions.onChangeCompletedAt}>
            <IconText
              iconStart={<PageListCheckIcon />}
              text={t("sidebar_options_menu.mark_as_complete")}
            />
          </MenuItem>
        )
      )}

      {optionsMenuActions?.onCopyToCourse && (
        <>
          <MenuDivider />
          <SubMenu
            label={
              <IconText
                iconStart={<CopySendArrowIcon />}
                text={t("sidebar_options_menu.copy_to_course")}
              />
            }
            withArrow
          >
            <CopyToCourseSubMenu
              category="activity_page"
              onCancel={() => {
                menuRef.current?.closeMenu();
              }}
              onConfirm={(courseIds) => {
                trackEvent("Topic - Copy to course", {
                  ...BUTTON_EVENT_CATEGORY,
                });
                optionsMenuActions?.onCopyToCourse(courseIds);
                menuRef.current?.closeMenu();
              }}
            />
          </SubMenu>
        </>
      )}
    </SidebarOptionsMenu>
  );
};
